/**
 * NotificationsContext — In-app notification feed.
 *
 * This is the scaffold layer. The Sidebar bell + dropdown reads from this context.
 * Specific producers (off-site clock-in, overdue materials, etc.) call `notify(...)`.
 *
 * Persistence is intentionally *in-memory* for now — entries clear on reload. A future
 * phase can swap the store to an `app_notifications` Supabase table without touching
 * any of the producer call sites.
 */
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { nuevoId } from '../utils/nuevoId';

export type NotificationSeverity = 'info' | 'warn' | 'danger';
export type NotificationType =
    | 'off-site-clock-in'
    | 'material-overdue'
    | 'geofence-missing'
    | 'task-completed'
    | 'project-status'
    | 'generic';

export interface AppNotification {
    id: string;
    type: NotificationType;
    severity: NotificationSeverity;
    body: string;
    timestamp: number;
    projectId?: string;
    read: boolean;
}

interface NotificationsContextType {
    notifications: AppNotification[];
    unreadCount: number;
    notify: (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
    markRead: (id: string) => void;
    markAllRead: () => void;
    clear: (id: string) => void;
    clearAll: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
    const ctx = useContext(NotificationsContext);
    if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
    return ctx;
};

const MAX_NOTIFICATIONS = 50;

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);

    const notify = useCallback((n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
        setNotifications(prev => {
            const next: AppNotification = {
                ...n,
                id: nuevoId(),
                timestamp: Date.now(),
                read: false,
            };
            // Newest first, capped at MAX_NOTIFICATIONS
            return [next, ...prev].slice(0, MAX_NOTIFICATIONS);
        });
    }, []);

    const markRead = useCallback((id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }, []);

    const markAllRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    const clear = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationsContext.Provider value={{ notifications, unreadCount, notify, markRead, markAllRead, clear, clearAll }}>
            {children}
        </NotificationsContext.Provider>
    );
};
